import { takeEvery, take, put, select, race, getContext, call } from 'redux-saga/effects';

import {
  startCall,
  acceptCallByRemote,
  setLocalStream,
  setRemoteStream,
  callFinishByRemoteActions,
  callFinishActions,
  endCall,
  setStatus,
  sendViewerSettingsByRemote,
} from './actions';
import { sendCallRing } from '../communication/actions';
import authSelectors from '../auth/selectors';
import appSelectors from '../app/selectors';
import { createMasterConnectionChannel, createSlaveConnectionChannel } from './kvsWebRTC';
import { getLogger } from '../../logger/index';
import { fetchAndPropagateKVSSettingsSaga } from './kvsSettings';
import { deactivateCustomerVideoCallSaga } from './sagaCustomer';

const logger = getLogger('communicationVideo :: outgoingCall');

function* outgoingCallSaga({ type, to, from, customerVideoCall, customerChat }) {
  let settings;
  let connectionChannel;

  try {
    const authName = yield select(authSelectors.getName);
    const authId = yield select(authSelectors.getId);
    const isEmployee = yield select(authSelectors.isEmployee);

    if (!isEmployee) {
      yield call(outgoingCustomerCallSaga, { type, to, from, customerVideoCall, customerChat });
      return;
    }

    const socketArg = {
      from: isEmployee ? authId : from.id,
      fromName: isEmployee ? authName : from.name,
      to: to.id,
      toName: to.name,
      customerVideoCall,
    };

    logger.debug('Send ring to remote');
    yield put(sendCallRing(type === 'video', socketArg));

    logger.debug('wait for answare');
    const isAccepted = yield call(waitForAnsware, type, socketArg);
    if (!isAccepted) {
      logger.debug('Stop saga because the call was not accepted');
      return;
    }

    yield put(setStatus({ source: 'presenter', info: 'preparing' }));
    logger.debug('Fetch KinesisVideoStream settings');
    const kvsSettings = yield call(fetchAndPropagateKVSSettingsSaga, to, type, from);
    if (!kvsSettings) {
      logger.error('Failed while fetching KVS settings');
      yield put(endCall);
    }
    // settings for master connection
    settings = {
      ...kvsSettings,
      channelEndpoints: kvsSettings.masterChannelEndpoints,
      iceServers: kvsSettings.masterIceServers,
    };

    logger.debug('enable camera and mic for connection');
    const mediaDevices = yield getContext('mediaDevices');
    const isAppActive = yield select(appSelectors.isActive);

    logger.debug('Create WebRTC connection');
    const RTCPeerConnection = yield getContext('RTCPeerConnection');
    connectionChannel = createMasterConnectionChannel({
      settings,
      RTCPeerConnection,
      type: isAppActive ? type : 'audio',
      mediaDevices,
    });

    logger.debug('watch connection events');
    yield race({
      watch: call(watchOutgoingConnectionEvents, connectionChannel, to),
      cancel: take([...callFinishActions, ...callFinishByRemoteActions]),
    });

    logger.debug('finish watching events on end call');
  } catch (e) {
    logger.error(e);
  } finally {
    logger.debug('outgoingCallSaga closed');
    if (connectionChannel) {
      connectionChannel.close();
    }

    if (settings) {
      logger.debug('Deactivate customomer video call', settings.customerVideoCallId);
      yield call(deactivateCustomerVideoCallSaga, settings.customerVideoCallId);
    }
  }
}

function* outgoingCustomerCallSaga({ type, to, from, customerVideoCall, customerChat }) {
  let connectionChannel;
  try {
    const socketArg = {
      from: from.id,
      fromName: from.name,
      to: to.id,
      toName: to.name,
      customerVideoCall,
      customerChat,
    };

    logger.debug('Send ring to remote');
    yield put(sendCallRing(type === 'video', socketArg));

    logger.debug('wait for answare');
    const isAccepted = yield call(waitForAnsware, type, socketArg);
    if (!isAccepted) {
      logger.debug('Stop saga because the call was not accepted');
      return;
    }

    logger.debug('wait for settings');
    const {
      payload: { settings },
    } = yield take(sendViewerSettingsByRemote);

    logger.debug('Create WebRTC connection');
    const mediaDevices = yield getContext('mediaDevices');
    const isAppActive = yield select(appSelectors.isActive);
    const RTCPeerConnection = yield getContext('RTCPeerConnection');
    connectionChannel = createSlaveConnectionChannel({
      settings: { ...settings, to: to.id },
      RTCPeerConnection,
      type: isAppActive ? type : 'audio',
      mediaDevices,
    });

    logger.debug('watch connection events');
    yield call(watchOutgoingConnectionEvents, connectionChannel, to);
  } catch (e) {
    logger.error(e);
  } finally {
    logger.debug('outgoingCustomerCallSaga closed');
    if (connectionChannel) {
      connectionChannel.close();
    }
  }
}

function* waitForAnsware(type, socketArg) {
  try {
    // wait max 30s and than end the call or stop on cancel
    const { cancelled, rejectedByRemote } = yield race({
      callAccepted: take(action => {
        logger.debug('Wait for Answare', acceptCallByRemote.toString(), action, 'Socker Args', socketArg);
        return action.type === acceptCallByRemote.toString(); //&& action.payload.from.id === socketArg.to;
      }),
      cancelled: take(callFinishActions),
      rejectedByRemote: take(callFinishByRemoteActions),
      // timeout: delay(30000, true),
    });
    if (cancelled) {
      logger.debug('Cancel call before answare');
      return false;
    }
    if (rejectedByRemote) {
      logger.debug('Reject call by remote before answare');
      return false;
    }
  } catch (e) {
    logger.error(e);
  }
  return true;
}

export function* watchOutgoingConnectionEvents(connectionChannel, user) {
  try {
    const running = true;
    while (running) {
      const { eventName, args } = yield take(connectionChannel);

      const actionMap = {
        localStream: function*() {
          logger.debug('Set local stream');
          yield put(setLocalStream(args));
        },
        remoteStream: function*() {
          logger.debug('Set remote stream');
          yield put(setRemoteStream(args, user));
        },
        statusUpdate: function*() {
          yield put(setStatus(args));
        },
      };

      if (actionMap[eventName]) {
        yield call(actionMap[eventName]);
      }
    }
  } catch (e) {
    logger.error(e);
  } finally {
    yield put(setStatus({ source: 'presenter', info: 'disconnected' }));
    logger.debug('Left watchOutgoingConnectionEvents');
  }
}

function* startCallSaga({ payload: { type, to, from, customerVideoCall, customerChat } }) {
  yield put(setStatus({ source: 'presenter', info: 'calling' }));
  yield race({
    watch: call(outgoingCallSaga, { type, from, to, customerVideoCall, customerChat }),
    cancel: take([...callFinishActions, ...callFinishByRemoteActions]),
  });

  logger.debug('finish watching outgoing call events on end call');
}

function* watchOutgoingCallActions() {
  try {
    logger.debug('Watch outgoing call actions');
    yield takeEvery(startCall, startCallSaga);
  } catch (e) {
    logger.error(e);
  }
}
export default watchOutgoingCallActions;
